import { useEffect, useState } from 'react';
import { homePageService } from '@/api/services';
import { usePublicCatalogData } from '@/components/PublicCatalogData';
import { homeCmsIconMap as iconMap } from '@/utils/homeCmsIconMap';

/**
 * Hook to fetch and manage home page data
 * @param {string} sectionKey - Optional section key to fetch a specific section
 * @returns {object} { sections, section, loading, error }
 */
export const useHomePage = (sectionKey = null, shouldFetch = true) => {
  const catalog = usePublicCatalogData();
  const contextSections = !sectionKey && catalog?.homeSections != null ? catalog.homeSections : null;
  const [sections, setSections] = useState(contextSections || []);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(contextSections == null && shouldFetch);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (contextSections != null) {
      setSections(contextSections);
      setLoading(false);
      return;
    }
    if (!shouldFetch) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (sectionKey) {
          const response = await homePageService.getSectionByKey(sectionKey);
          if (response.data?.success) {
            setSection(response.data.data);
          }
        } else {
          const response = await homePageService.getAllSections();
          if (response.data?.success) {
            setSections(response.data.data || []);
          }
        }
      } catch (err) {
        console.error('Error fetching home page data:', err);
        setError(err.message || 'Failed to load home page data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sectionKey, shouldFetch, contextSections]);

  return { sections, section, loading, error };
};

/**
 * Get a specific section from the sections array by key
 * @param {array} sections - Array of sections
 * @param {string} key - Section key
 * @returns {object|null} Section object or null
 */
export const getHomeSectionByKey = (sections, key) => {
  return sections.find(sec => sec.section_key === key) || null;
};

export { iconMap };

export default useHomePage;
